/**
 * Authenticated fetch wrapper with single-flight refresh on 401.
 *
 *   1. attach `Authorization: Bearer <access>` from storage
 *   2. on 401, call POST /api/auth/refresh exactly once per concurrent burst
 *      (in-flight promise is shared)
 *   3. on refresh success, store the new pair and retry the original request
 *      once with the new access token
 *   4. on refresh failure or second 401, clear storage and throw
 *      `UnauthorizedError`. The auth-guard layout converts that into a
 *      navigate-to-/login.
 *
 * Cross-tab coordination is intentionally NOT implemented in v1. Two tabs
 * may both observe a 401 and both call refresh; the api's atomic rotation
 * lets one win and force-logs-out the other on its next call. Acceptable
 * UX for an internal tool — see auth design doc.
 */
import { clearTokens, loadTokens, saveTokens } from "./auth-storage";
import { getEffectiveRole } from "./effective-role";

const AUTH_BYPASSED_PATHS = new Set([
  "/api/auth/login",
  "/api/auth/refresh",
  "/api/auth/logout",
  "/api/health",
]);

function pathOf(input: RequestInfo): string {
  if (typeof input === "string") return input.split("?")[0] ?? "";
  if (input instanceof Request) return new URL(input.url, "http://x").pathname;
  return "";
}

export class UnauthorizedError extends Error {
  constructor() {
    super("unauthorized");
    this.name = "UnauthorizedError";
  }
}

let refreshInFlight: Promise<{ access: string; refresh: string } | null> | null = null;

async function startRefresh(
  refreshToken: string,
): Promise<{ access: string; refresh: string } | null> {
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { accessToken: string; refreshToken: string };
    const pair = { access: body.accessToken, refresh: body.refreshToken };
    saveTokens(pair);
    return pair;
  } catch {
    return null;
  }
}

function getOrStartRefresh(refreshToken: string) {
  if (!refreshInFlight) {
    refreshInFlight = startRefresh(refreshToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function apiFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const tokens = loadTokens();
  const headers = new Headers(init?.headers ?? {});
  if (tokens?.access && !headers.has("authorization")) {
    headers.set("authorization", `Bearer ${tokens.access}`);
  }
  if (!AUTH_BYPASSED_PATHS.has(pathOf(input)) && !headers.has("x-effective-role")) {
    const effective = getEffectiveRole();
    if (effective) headers.set("x-effective-role", effective);
  }
  let res = await fetch(input, { ...init, headers });
  if (res.status !== 401 || !tokens?.refresh) {
    if (res.status === 401) {
      // Stale access token in storage with no refresh path → clear so
      // subsequent navigations don't replay the dead token.
      clearTokens();
      throw new UnauthorizedError();
    }
    return res;
  }

  const refreshed = await getOrStartRefresh(tokens.refresh);
  if (!refreshed) {
    clearTokens();
    throw new UnauthorizedError();
  }
  const retryHeaders = new Headers(headers);
  retryHeaders.set("authorization", `Bearer ${refreshed.access}`);
  res = await fetch(input, { ...init, headers: retryHeaders });
  if (res.status === 401) {
    clearTokens();
    throw new UnauthorizedError();
  }
  return res;
}
